import { ResourceNameTypeEnum } from '@bondsports/types';
import { useRouter } from 'next/router';
import { RecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { Area } from 'types/checkout';
import { getCheckoutStore } from 'stores/checkoutStores';
import { RentalCheckoutStore } from 'stores/rentalCheckoutStore';
import { resourceApi } from '../../../../../lib/api/resourceApi';
import { Product, ProductPackage } from '../../../../../types/product';
import { PaginatedResourceResponse } from '../../../../../types/resources';
import { DEFAULT_RESOURCE_QTY, DEFAULT_RESOURCES_PER_PAGE } from '../types/checkout.consts';
import { createSelectAllInfo, createSelectedResourcesObject } from '../lib/checkout.util';
import { toTimezoneIndependent } from 'lib/dates';
import { i18n } from 'lib/i18n';

const useCheckoutStore = (area?: Area) => {
	const router = useRouter();
	area = area || (router.pathname.split('/')[2] as Area); // e.g "/checkout/program/application"

	const checkoutStore = getCheckoutStore(area);

	if (!checkoutStore) {
		throw new Error(i18n.checkout.payment.charge.missingDataError);
	}
	const [checkoutState, setCheckoutState] = useRecoilState(checkoutStore.checkout as RecoilState<RentalCheckoutStore>);
	const answersForInit = useRecoilValue(checkoutStore?.checkoutEventApplications);

	const fetchProductsResourcesData = async (selectedProductId: number, page = 1, itemsPerPage = DEFAULT_RESOURCES_PER_PAGE, filterStartDate?: string, filterEndDate?: string): Promise<PaginatedResourceResponse> => {
		const startDate: string = filterStartDate ? toTimezoneIndependent(filterStartDate) : '';
		const endDate: string = filterEndDate ? toTimezoneIndependent(filterEndDate) : '';
		const responseResource: PaginatedResourceResponse = await resourceApi.getPaginatedProductResources<PaginatedResourceResponse>(selectedProductId, {
			page,
			itemsPerPage,
			futureResources: true,
			...(!!startDate && { startDate }),
			...(!!endDate && { endDate }),
		});
		setCheckoutState(state => ({
			...state,
			productResources: responseResource ?? [],
		}));
		return responseResource;
	};

	const setDefaultSelectedResourcesState = (checkoutState: RentalCheckoutStore, resourcesRes: PaginatedResourceResponse) => {
		const product: Product | undefined = checkoutState?.selectedProduct;
		const quantity: number = product?.quantity ? Number(product.quantity) : DEFAULT_RESOURCE_QTY;
		const resourceType: ResourceNameTypeEnum = (resourcesRes.sharedData?.resourceType || ResourceNameTypeEnum.EVENT) as ResourceNameTypeEnum;

		if (product && quantity && resourceType) {
			setCheckoutState(state => ({
				...state,
				...(!product.isAll ? createSelectedResourcesObject(quantity, resourceType, []) : createSelectAllInfo(quantity, resourceType)),
			}));
		}
		else {
			setCheckoutState(state => ({
				...state,
				selectedResources: {
					quantity: 0,
					resourceType: ResourceNameTypeEnum.EVENT,
					resources: [],
				},
			}));
		}
	};

	const setProductPackage = (packageRes: ProductPackage) => {
		setCheckoutState(state => ({
			...state,
			selectedProductPackage: packageRes,
		}));
	};

	return {
		checkoutStore,
		checkoutState,
		answersForInit,
		area,
		setCheckoutState,
		fetchProductsResourcesData,
		setDefaultSelectedResourcesState,
		setProductPackage,
	};
}

export default useCheckoutStore;
